import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Alert, AlertDescription, AlertTitle, Badge, Button, Card, CardContent } from 'shared/src/components/ui/new';
import { type ProseMirrorNode, getSafeHref, getYouTubeEmbedUrl, isDocumentEmpty, parseLearnHouseContent } from './learnHouseContent';
import { LmsMediaProvider, useLmsMedia } from './lmsMediaContext';

const INLINE_IMAGE_DATA_URI = /^data:image\/(?:jpeg|png|gif|webp);base64,[A-Za-z0-9+/]+={0,2}$/;

function getSafeInlineImageSrc(src: unknown): string | null {
  if (typeof src !== 'string' || src.trim() === '') {
    return null;
  }

  const value = src.trim();
  return INLINE_IMAGE_DATA_URI.test(value) ? value : null;
}

function getSafeStaticSrc(src: unknown): string | null {
  if (typeof src !== 'string' || src.trim() === '') {
    return null;
  }

  const value = src.trim();
  const inlineImage = getSafeInlineImageSrc(value);
  if (inlineImage) {
    return inlineImage;
  }

  if (value.startsWith('/') && !value.startsWith('//') && !value.includes('..')) {
    return value;
  }

  return getSafeHref(value);
}

function parseBlockMediaAttrs(attrs: Record<string, unknown> | undefined, fallbackName: string) {
  const blockObject = attrs?.blockObject && typeof attrs.blockObject === 'object' && !Array.isArray(attrs.blockObject) ? (attrs.blockObject as Record<string, unknown>) : null;
  const content = blockObject?.content && typeof blockObject.content === 'object' && !Array.isArray(blockObject.content) ? (blockObject.content as Record<string, unknown>) : null;
  const fileId = attrString(content?.file_id);
  const fileName = attrString(content?.file_name) || fileId || fallbackName;
  const activityUuidFromBlock = attrString(content?.activity_uuid);
  const staticSrc = getSafeStaticSrc(attrs?.src);
  const unsplashUrl = getSafeHref(attrs?.unsplash_url);

  return {
    fileId,
    fileName,
    fileFormat: attrString(content?.file_format),
    blockUuid: attrString(blockObject?.block_uuid),
    activityUuidFromBlock,
    staticSrc,
    unsplashUrl,
    alignment: attrs?.alignment,
  };
}

function useBlockMediaObjectUrl(kind: 'image' | 'pdf', fileId: string, activityUuidFromBlock: string, blockUuid: string, fileFormat: string, skipProxy = false) {
  const { courseUuid, activityUuid: contextActivityUuid, fetchers } = useLmsMedia();
  const activityUuid = contextActivityUuid || activityUuidFromBlock;
  const canProxy = Boolean(courseUuid && activityUuid && fileId && !skipProxy);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(canProxy);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!canProxy || !courseUuid || !activityUuid || !fileId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    let createdUrl: string | null = null;

    async function load() {
      if (attempt < 0) {
        return;
      }
      setIsLoading(true);
      setError(null);
      setObjectUrl(null);

      try {
        const request = { fileId, blockUuid: blockUuid || undefined, fileFormat: fileFormat || undefined };
        const blob = kind === 'image' ? await fetchers.getBlockImage(courseUuid, activityUuid, request) : await fetchers.getBlockPdf(courseUuid, activityUuid, request);
        if (cancelled) {
          return;
        }
        createdUrl = URL.createObjectURL(blob);
        setObjectUrl(createdUrl);
      } catch (loadError) {
        console.error(loadError);
        if (!cancelled) {
          setError(`Failed to load ${kind}.`);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    void load();

    return () => {
      cancelled = true;
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
      }
    };
  }, [attempt, blockUuid, canProxy, courseUuid, activityUuid, fetchers, fileFormat, fileId, kind]);

  return { courseUuid, activityUuid, objectUrl, error, isLoading, retry: () => setAttempt((value) => value + 1) };
}

function MediaLoadNotice({ kind, missing, onRetry }: { kind: 'image' | 'document'; missing?: boolean; onRetry?: () => void }) {
  const label = kind === 'image' ? 'image' : 'document';

  return (
    <div className="my-3 rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
      <p>{missing ? `This ${label} isn't available.` : `This ${label} didn't load.`}</p>
      {onRetry ? (
        <Button type="button" variant="outline" size="sm" className="mt-2" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </div>
  );
}

function BlockImage({ node }: { node: ProseMirrorNode }) {
  const { fileId, fileName, fileFormat, blockUuid, activityUuidFromBlock, staticSrc, unsplashUrl, alignment } = parseBlockMediaAttrs(
    node.attrs as Record<string, unknown> | undefined,
    'Image',
  );
  const { objectUrl, error, isLoading, retry } = useBlockMediaObjectUrl('image', fileId, activityUuidFromBlock, blockUuid, fileFormat, Boolean(getSafeInlineImageSrc(staticSrc)));
  const resolvedSrc = unsplashUrl ?? objectUrl ?? (error ? null : staticSrc);
  const justify = alignmentJustify(alignment);
  const [displayFailed, setDisplayFailed] = useState(false);
  const tryAgain = () => {
    setDisplayFailed(false);
    retry();
  };

  if (isLoading && !resolvedSrc) {
    return <div className="my-3 text-sm text-gray-500">Loading image…</div>;
  }

  if (!resolvedSrc || displayFailed) {
    return <MediaLoadNotice kind="image" missing={!error && !displayFailed} onRetry={error || displayFailed ? tryAgain : undefined} />;
  }

  return (
    <div className={`my-4 flex ${justify}`}>
      <figure className="w-full">
        <img
          src={resolvedSrc}
          alt={fileName}
          className="h-auto w-full rounded-md border border-gray-200 bg-white object-contain shadow-sm"
          onError={() => setDisplayFailed(true)}
        />
      </figure>
    </div>
  );
}

function BlockPdf({ node }: { node: ProseMirrorNode }) {
  const { fileId, fileName, fileFormat, blockUuid, activityUuidFromBlock, staticSrc } = parseBlockMediaAttrs(node.attrs as Record<string, unknown> | undefined, 'Document');
  const { objectUrl, error, isLoading, retry } = useBlockMediaObjectUrl('pdf', fileId, activityUuidFromBlock, blockUuid, fileFormat);
  const resolvedSrc = objectUrl ?? (error ? null : staticSrc);

  if (isLoading && !resolvedSrc) {
    return <div className="my-3 text-sm text-gray-500">Loading PDF…</div>;
  }

  if (!resolvedSrc) {
    return <MediaLoadNotice kind="document" missing={!error} onRetry={error ? retry : undefined} />;
  }

  return (
    <div className="my-4">
      <div className="mb-1 text-xs font-medium text-gray-500">{fileName}</div>
      {isLoading && !objectUrl ? <div className="mb-1 text-xs text-gray-400">Loading PDF…</div> : null}
      <iframe title={fileName} src={resolvedSrc} className="h-[28rem] w-full rounded-md border border-gray-200 bg-white" />
    </div>
  );
}

function CalloutBlock({ node, variant, title }: { node: ProseMirrorNode; variant: 'information' | 'warning' | 'success' | 'destructive' | 'default'; title: string }) {
  return (
    <Alert variant={variant} className="my-3">
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>{renderChildren(node, 'callout')}</AlertDescription>
    </Alert>
  );
}

const ALIGNMENT_JUSTIFY: Record<string, string> = {
  left: 'justify-start',
  center: 'justify-center',
  right: 'justify-end',
};

const FLIP_SIZE_CLASS: Record<string, string> = {
  small: 'max-w-xs',
  sm: 'max-w-xs',
  medium: 'max-w-md',
  md: 'max-w-md',
  large: 'max-w-2xl',
  lg: 'max-w-2xl',
};

function attrString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function alignmentJustify(alignment: unknown): string {
  return ALIGNMENT_JUSTIFY[attrString(alignment).toLowerCase()] ?? 'justify-start';
}

function mapBadgeStatus(color: unknown): 'success' | 'warning' | 'error' | 'info' | 'pending' | undefined {
  const c = attrString(color).toLowerCase();
  if (!c) return undefined;
  if (c.includes('green') || c === 'success') return 'success';
  if (c.includes('yellow') || c.includes('orange') || c === 'warning') return 'warning';
  if (c.includes('red') || c === 'error' || c === 'danger') return 'error';
  if (c.includes('purple') || c === 'pending') return 'pending';
  if (c.includes('blue') || c === 'info') return 'info';
  return 'info';
}

function FlipCardFace({ label, body, hint }: { label: string; body: string; hint: string }) {
  return (
    <Card className="h-full w-full shadow-sm">
      <CardContent className="flex h-full flex-col justify-center p-4">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</div>
        <div className="text-sm">{body}</div>
        <div className="mt-3 text-xs text-gray-400">{hint}</div>
      </CardContent>
    </Card>
  );
}

function FlipCard({ question, answer, alignment, size }: { question: string; answer: string; alignment: unknown; size: unknown }) {
  const [flipped, setFlipped] = useState(false);
  const sizeClass = FLIP_SIZE_CLASS[attrString(size).toLowerCase()] ?? 'max-w-md';

  const toggle = () => setFlipped((value) => !value);

  return (
    <div className={`my-3 flex ${alignmentJustify(alignment)}`}>
      <button
        type="button"
        aria-pressed={flipped}
        className={`w-full cursor-pointer border-0 bg-transparent p-0 text-left ${sizeClass}`}
        style={{ perspective: '1000px' }}
        onClick={toggle}
      >
        <div
          className="w-full transition-transform duration-500 ease-in-out"
          style={{
            display: 'grid',
            gridTemplate: '1fr / 1fr',
            transformStyle: 'preserve-3d',
            transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
          }}
        >
          <div
            style={{
              gridArea: '1 / 1',
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
            }}
          >
            <FlipCardFace label="Question" body={question || 'No question provided.'} hint="Click to reveal answer" />
          </div>
          <div
            style={{
              gridArea: '1 / 1',
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
              transform: 'rotateY(180deg)',
            }}
          >
            <FlipCardFace label="Answer" body={answer || 'No answer provided.'} hint="Click to show question" />
          </div>
        </div>
      </button>
    </div>
  );
}

export function UnsupportedBlock({ label, payload }: { label: string; payload: unknown }) {
  if (import.meta.env.PROD) {
    return null;
  }

  return (
    <details className="my-3 rounded-md border border-amber-300 bg-amber-50">
      <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-amber-900">{label}</summary>
      <pre className="max-h-80 overflow-auto border-t border-amber-200 bg-white p-3 text-xs text-gray-700">{JSON.stringify(payload, null, 2)}</pre>
    </details>
  );
}

export function LearnHouseYoutubeEmbed({ source, title = 'YouTube video' }: { source: unknown; title?: string }) {
  const embedUrl = getYouTubeEmbedUrl(source);
  if (!embedUrl) {
    return <UnsupportedBlock label="Unsupported YouTube source" payload={source ?? null} />;
  }

  return (
    <div className="relative my-4 w-full overflow-hidden rounded-md bg-black" style={{ aspectRatio: '16 / 9' }}>
      <iframe
        src={embedUrl}
        title={title}
        className="absolute inset-0 h-full w-full border-0"
        sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
        allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}

type QuizAnswer = {
  answerId: string;
  text: string;
  correct: boolean;
};

type QuizQuestion = {
  questionId: string;
  question: string;
  type: string;
  answers: QuizAnswer[];
};

function answerLetter(index: number): string {
  return String.fromCharCode('A'.charCodeAt(0) + index);
}

function normalizeQuizQuestions(raw: unknown): QuizQuestion[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw.map((item, questionIndex) => {
    const question = (item ?? {}) as Record<string, unknown>;
    const questionId = attrString(question.question_id || question.id) || `question-${questionIndex}`;
    const options = Array.isArray(question.answers) ? question.answers : Array.isArray(question.options) ? question.options : [];

    return {
      questionId,
      question: attrString(question.question || question.questionText || question.text),
      type: attrString(question.type) || 'multiple_choice',
      answers: options.map((option, answerIndex) => {
        const answer = (option ?? {}) as Record<string, unknown>;
        return {
          answerId: attrString(answer.answer_id || answer.id || answer.optionUUID) || `${questionId}-answer-${answerIndex}`,
          text: attrString(answer.answer || answer.text),
          correct: Boolean(answer.correct ?? answer.assigned_right_answer),
        };
      }),
    };
  });
}

function isQuestionCorrect(question: QuizQuestion, selectedIds: Set<string>): boolean {
  const correctIds = question.answers.filter((answer) => answer.correct).map((answer) => answer.answerId);
  if (correctIds.length === 0) {
    return selectedIds.size === 0;
  }
  if (correctIds.length !== selectedIds.size) {
    return false;
  }
  return correctIds.every((id) => selectedIds.has(id));
}

function QuizBlock({ node }: { node: ProseMirrorNode }) {
  const questions = useMemo(() => normalizeQuizQuestions(node.attrs?.questions), [node.attrs?.questions]);
  const [selectedByQuestion, setSelectedByQuestion] = useState<Record<string, string[]>>({});
  const [submitted, setSubmitted] = useState(false);
  const [allCorrect, setAllCorrect] = useState(false);

  if (questions.length === 0) {
    return <UnsupportedBlock label="Quiz has no questions" payload={node} />;
  }

  const hasSelection = Object.values(selectedByQuestion).some((ids) => ids.length > 0);

  const toggleAnswer = (questionId: string, answerId: string) => {
    if (submitted) {
      return;
    }

    setSelectedByQuestion((current) => {
      const selected = new Set(current[questionId] ?? []);
      if (selected.has(answerId)) {
        selected.delete(answerId);
      } else {
        selected.add(answerId);
      }
      return { ...current, [questionId]: [...selected] };
    });
  };

  const handleSubmit = () => {
    const passed = questions.every((question) => isQuestionCorrect(question, new Set(selectedByQuestion[question.questionId] ?? [])));
    setAllCorrect(passed);
    setSubmitted(true);
  };

  const handleReset = () => {
    setSelectedByQuestion({});
    setSubmitted(false);
    setAllCorrect(false);
  };

  return (
    <section className="my-4 rounded-md border border-gray-200 bg-gray-50 p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Practice</div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={handleReset} disabled={!hasSelection && !submitted}>
            Reset
          </Button>
          <Button type="button" size="sm" onClick={handleSubmit} disabled={submitted || !hasSelection || questions.length === 0}>
            Check answers
          </Button>
        </div>
      </div>

      {questions.map((question, questionIndex) => {
        const selected = new Set(selectedByQuestion[question.questionId] ?? []);

        return (
          <fieldset key={question.questionId} className="mb-4 last:mb-0" disabled={submitted}>
            <legend className="mb-2 text-sm font-medium">
              {questionIndex + 1}. {question.question.trim() || 'Untitled question'}
            </legend>
            {question.answers.length === 0 ? (
              <div className="text-sm text-gray-500">No answers provided.</div>
            ) : (
              <ul className="space-y-1.5">
                {question.answers.map((answer, answerIndex) => {
                  const isSelected = selected.has(answer.answerId);
                  const isCorrectReveal = submitted && answer.correct;
                  const isWrongSelection = submitted && isSelected && !answer.correct;
                  const letter = answerLetter(answerIndex);

                  let rowClass = 'border-gray-200 bg-white hover:bg-gray-50';
                  let chipClass = 'bg-gray-100 text-gray-600';
                  let textClass = 'text-gray-800';

                  if (!submitted && isSelected) {
                    rowClass = 'border-blue-300 bg-blue-50';
                    chipClass = 'bg-blue-500 text-white';
                  }
                  if (isCorrectReveal) {
                    rowClass = 'border-emerald-300 bg-emerald-50';
                    chipClass = 'bg-emerald-500 text-white';
                    textClass = 'font-medium text-emerald-900';
                  }
                  if (isWrongSelection) {
                    rowClass = 'border-red-300 bg-red-50';
                    chipClass = 'bg-red-500 text-white';
                    textClass = 'font-medium text-red-900';
                  }
                  if (submitted && !answer.correct && !isSelected) {
                    rowClass = 'border-gray-200 bg-white opacity-60';
                    chipClass = 'bg-gray-100 text-gray-400';
                    textClass = 'text-gray-500';
                  }

                  return (
                    <li key={answer.answerId}>
                      <button
                        type="button"
                        aria-pressed={isSelected}
                        disabled={submitted}
                        onClick={() => toggleAnswer(question.questionId, answer.answerId)}
                        className={`flex w-full items-center gap-2 rounded-md border px-2 py-1.5 text-left text-sm transition-colors disabled:cursor-default ${rowClass}`}
                      >
                        <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded text-[11px] font-bold ${chipClass}`}>
                          {isCorrectReveal ? '✓' : isWrongSelection ? '✕' : letter}
                        </span>
                        <span className={textClass}>{answer.text.trim() || 'Untitled answer'}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </fieldset>
        );
      })}

      {submitted ? (
        <output className={`mt-3 inline-flex rounded-md px-2.5 py-1 text-xs font-medium ${allCorrect ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
          {allCorrect ? 'All answers correct.' : 'Some answers are incorrect. Reset to try again.'}
        </output>
      ) : (
        <p className="mt-3 text-xs text-gray-500">Select one or more options, then check your answers. Results stay in this browser only.</p>
      )}
    </section>
  );
}

function renderText(node: ProseMirrorNode, key: string): ReactNode {
  let element: ReactNode = node.text ?? '';

  for (const mark of node.marks ?? []) {
    switch (mark.type) {
      case 'bold':
        element = <strong>{element}</strong>;
        break;
      case 'italic':
        element = <em>{element}</em>;
        break;
      case 'strike':
        element = <s>{element}</s>;
        break;
      case 'code':
        element = <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-[0.9em]">{element}</code>;
        break;
      case 'link': {
        const href = getSafeHref(mark.attrs?.href);
        element = href ? (
          <a href={href} target="_blank" rel="noopener noreferrer" className="text-enhancetitle underline">
            {element}
          </a>
        ) : (
          <span className="underline decoration-dotted">{element}</span>
        );
        break;
      }
      default:
        break;
    }
  }

  return <span key={key}>{element}</span>;
}

function renderChildren(node: ProseMirrorNode, keyPrefix: string): ReactNode[] {
  return (node.content ?? []).map((child, index) => renderNode(child, `${keyPrefix}.${index}`));
}

function renderNode(node: ProseMirrorNode, key: string): ReactNode {
  switch (node.type) {
    case 'text':
      return renderText(node, key);

    case 'paragraph': {
      const children = renderChildren(node, key);
      return (
        <p key={key} className="my-2 leading-relaxed">
          {children.length > 0 ? children : <br />}
        </p>
      );
    }

    case 'heading': {
      const level = Number(node.attrs?.level ?? 1);
      const safeLevel = level >= 1 && level <= 6 ? level : 1;
      const Tag = `h${safeLevel}` as 'h1';
      const sizes: Record<number, string> = {
        1: 'text-2xl',
        2: 'text-xl',
        3: 'text-lg',
        4: 'text-base',
        5: 'text-sm',
        6: 'text-sm',
      };
      return (
        <Tag key={key} className={`mt-5 mb-2 font-semibold ${sizes[safeLevel]}`}>
          {renderChildren(node, key)}
        </Tag>
      );
    }

    case 'hardBreak':
      return <br key={key} />;

    case 'horizontalRule':
      return <hr key={key} className="my-5 border-gray-200" />;

    case 'blockquote':
      return (
        <blockquote key={key} className="my-3 border-l-4 border-gray-300 pl-4 text-gray-700 italic">
          {renderChildren(node, key)}
        </blockquote>
      );

    case 'bulletList':
      return (
        <ul key={key} className="my-2 list-disc space-y-1 pl-6">
          {renderChildren(node, key)}
        </ul>
      );

    case 'orderedList':
      return (
        <ol key={key} start={Number(node.attrs?.start ?? 1)} className="my-2 list-decimal space-y-1 pl-6">
          {renderChildren(node, key)}
        </ol>
      );

    case 'listItem':
      return <li key={key}>{renderChildren(node, key)}</li>;

    case 'codeBlock':
      return (
        <pre key={key} className="my-3 overflow-auto rounded-md bg-gray-900 p-3 text-xs text-gray-100">
          <code>{(node.content ?? []).map((child) => child.text ?? '').join('')}</code>
        </pre>
      );

    case 'table':
      return (
        <div key={key} className="my-3 overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <tbody>{renderChildren(node, key)}</tbody>
          </table>
        </div>
      );

    case 'tableRow':
      return <tr key={key}>{renderChildren(node, key)}</tr>;

    case 'tableHeader':
      return (
        <th
          key={key}
          colSpan={Number(node.attrs?.colspan ?? 1)}
          rowSpan={Number(node.attrs?.rowspan ?? 1)}
          className="border border-gray-300 bg-gray-50 p-2 text-left font-semibold"
        >
          {renderChildren(node, key)}
        </th>
      );

    case 'tableCell':
      return (
        <td key={key} colSpan={Number(node.attrs?.colspan ?? 1)} rowSpan={Number(node.attrs?.rowspan ?? 1)} className="border border-gray-300 p-2 align-top">
          {renderChildren(node, key)}
        </td>
      );

    case 'blockQuiz':
      return <QuizBlock key={key} node={node} />;

    case 'youtube':
      return <LearnHouseYoutubeEmbed key={key} source={node.attrs?.src ?? node.attrs?.videoId} />;

    case 'badge': {
      const emoji = attrString(node.attrs?.emoji);
      const status = mapBadgeStatus(node.attrs?.color);
      return (
        <Badge key={key} {...(status ? { status } : { variant: 'outline' })} className="my-1">
          {emoji ? `${emoji} ` : null}
          {renderChildren(node, key)}
        </Badge>
      );
    }

    case 'button': {
      const emoji = attrString(node.attrs?.emoji);
      const href = getSafeHref(node.attrs?.link);
      const label = (
        <>
          {emoji ? `${emoji} ` : null}
          {renderChildren(node, key)}
        </>
      );

      return (
        <div key={key} className={`my-3 flex ${alignmentJustify(node.attrs?.alignment)}`}>
          {href ? (
            <Button asChild>
              <a href={href} target="_blank" rel="noopener noreferrer">
                {label}
              </a>
            </Button>
          ) : (
            <Button disabled>{label}</Button>
          )}
        </div>
      );
    }

    case 'flipcard':
      return <FlipCard key={key} question={attrString(node.attrs?.question)} answer={attrString(node.attrs?.answer)} alignment={node.attrs?.alignment} size={node.attrs?.size} />;

    case 'blockImage':
      return <BlockImage key={key} node={node} />;

    case 'blockPDF':
      return <BlockPdf key={key} node={node} />;

    case 'calloutInfo':
      return <CalloutBlock key={key} node={node} variant="information" title="Info" />;

    case 'calloutWarning':
      return <CalloutBlock key={key} node={node} variant="warning" title="Warning" />;

    default:
      return <UnsupportedBlock key={key} label={`Unsupported block: ${node.type ?? 'unknown'}`} payload={node} />;
  }
}

export function LearnHouseRenderedNode({ node }: { node: ProseMirrorNode }) {
  return <>{renderNode(node, 'frozen')}</>;
}

export type LearnHouseContentRendererProps = {
  content: unknown;
  courseUuid?: string;
  activityUuid?: string;
};

export default function LearnHouseContentRenderer({ content, courseUuid, activityUuid }: LearnHouseContentRendererProps) {
  const doc = useMemo(() => parseLearnHouseContent(content), [content]);

  if (!doc) {
    return <UnsupportedBlock label="Content is not a renderable rich text document" payload={content ?? null} />;
  }

  if (isDocumentEmpty(doc)) {
    return <div className="text-sm text-gray-500">This activity has no content yet.</div>;
  }

  return (
    <LmsMediaProvider courseUuid={courseUuid} activityUuid={activityUuid}>
      <div className="max-w-3xl text-sm text-gray-900">{renderChildren(doc, 'node')}</div>
    </LmsMediaProvider>
  );
}
